// Insurance eligibility check（Stedi 270/271）前端类型。
// 状态词汇表两个 surface 共用（Clients 列表列 / Financial 发票明细 Result 列），
// 同一个值在两处渲染必须一致，配置集中在 components/EligibilityStatusTag。

// 9 个列表状态 + 2 个仅出现在发票明细/详情页的状态（PAYER_NOT_MATCHED / CHECK_FAILED）
// + 2 个 detail 页结果卡才会出现的状态（SERVICE_NOT_COVERED / CONFLICTING_AMOUNTS）
export enum EEligibilityStatus {
    ELIGIBLE = 'ELIGIBLE',
    INACTIVE = 'INACTIVE',
    NEEDS_RECHECK = 'NEEDS_RECHECK',
    COULD_NOT_VERIFY = 'COULD_NOT_VERIFY',
    NO_COST_ESTIMATE = 'NO_COST_ESTIMATE',
    OUT_OF_NETWORK = 'OUT_OF_NETWORK',
    CHECKED = 'CHECKED',
    NEVER_CHECKED = 'NEVER_CHECKED',
    NO_INSURANCE = 'NO_INSURANCE',
    PAYER_NOT_MATCHED = 'PAYER_NOT_MATCHED',
    CHECK_FAILED = 'CHECK_FAILED',
    SERVICE_NOT_COVERED = 'SERVICE_NOT_COVERED',
    CONFLICTING_AMOUNTS = 'CONFLICTING_AMOUNTS',
}

// ---------- 后端契约（FRONTEND_INTEGRATION + ELIGIBILITY_STATUS_MATRIX）----------
// 后端 `_result.status` 是逗号分隔的多状态串（按声明优先级排序，首 token 为 primary，驱动 tag/gate）。
// 时间戳 ISO 无时区，按 UTC 理解后转用户时区；金额为美元 number（前端展示层 ×100 转美分）。

// EligibilityStatusEnum 全集（声明顺序 = 优先级）
export type TBackendEligibilityStatus =
    | 'SYSTEM_ERROR'
    | 'PAYER_DOWN'
    | 'UNMAPPED'
    | 'NOT_SET_UP_WITH_PAYER'
    | 'PATIENT_NOT_FOUND'
    | 'MEMBER_ID_ERROR'
    | 'DOB_MISMATCH'
    | 'DOB_MISSING'
    | 'GENDER_ON_POLICY'
    | 'INACTIVE'
    | 'SERVICE_NOT_COVERED'
    | 'CARVE_OUT'
    | 'OUT_OF_NETWORK'
    | 'CONFLICTING_AMOUNTS'
    | 'PLANWIDE_ONLY'
    | 'NO_PER_VISIT_PRICE'
    // 10/22：271 回了 200 但 benefitsInformation 为空（Stedi guide 的 "Missing detail"）。
    // payer 答了、只是没给信息——不是 SYSTEM_ERROR，也不是拒识；后端保证单独下发
    | 'NO_COVERAGE_DETAIL'
    | 'ACTIVE';

// Tier 1/2 拒识类 token（→ 修复卡；aaa.ts 码表按这些 key 建）。UNMAPPED 单独走 advanced search 流
export const ELIGIBILITY_REJECTION_STATUSES = new Set<string>([
    'SYSTEM_ERROR',
    'PAYER_DOWN',
    'NOT_SET_UP_WITH_PAYER',
    'PATIENT_NOT_FOUND',
    'MEMBER_ID_ERROR',
    'DOB_MISMATCH',
    'DOB_MISSING',
    'GENDER_ON_POLICY',
]);

// 后端 primary status → 前端展示词汇表（列表 tag / 发票 RESULT 列 / detail header 共用）。
// NEEDS_RECHECK 不在此表，也已不再被任何地方派生（2026-09-17 拍板：只显示后端给的状态）
export const ELIGIBILITY_TAG_BY_BACKEND_STATUS: Record<string, EEligibilityStatus> = {
    ACTIVE: EEligibilityStatus.ELIGIBLE,
    INACTIVE: EEligibilityStatus.INACTIVE,
    OUT_OF_NETWORK: EEligibilityStatus.OUT_OF_NETWORK,
    SERVICE_NOT_COVERED: EEligibilityStatus.SERVICE_NOT_COVERED,
    CONFLICTING_AMOUNTS: EEligibilityStatus.CONFLICTING_AMOUNTS,
    // 原型 demo「Plan-wide pricing」场景的 tag 就是 Eligible（tagState 的 partial 不成立）——
    // 只有计划级定价不改变「这份保险有效、这次问诊可以billing」这个结论，缺的是价格，
    // 由卡片里的「没拿到本次问诊价格」两句和 Not reported 格子去说
    PLANWIDE_ONLY: EEligibilityStatus.ELIGIBLE,
    NO_PER_VISIT_PRICE: EEligibilityStatus.NO_COST_ESTIMATE,
    CARVE_OUT: EEligibilityStatus.NO_COST_ESTIMATE,
    PATIENT_NOT_FOUND: EEligibilityStatus.COULD_NOT_VERIFY,
    MEMBER_ID_ERROR: EEligibilityStatus.COULD_NOT_VERIFY,
    DOB_MISMATCH: EEligibilityStatus.COULD_NOT_VERIFY,
    DOB_MISSING: EEligibilityStatus.COULD_NOT_VERIFY,
    GENDER_ON_POLICY: EEligibilityStatus.COULD_NOT_VERIFY,
    NOT_SET_UP_WITH_PAYER: EEligibilityStatus.COULD_NOT_VERIFY,
    SYSTEM_ERROR: EEligibilityStatus.CHECK_FAILED,
    // 原型 demo「Payer down」（AAA 42）走的是 rejected 分支 → Could not verify；
    // Check failed 在原型里只留给「请求根本没发出去」的 system error
    PAYER_DOWN: EEligibilityStatus.COULD_NOT_VERIFY,
    // Stacey 10-22 选 A：不归 Check failed，标签 Could not verify
    NO_COVERAGE_DETAIL: EEligibilityStatus.COULD_NOT_VERIFY,
    UNMAPPED: EEligibilityStatus.PAYER_NOT_MATCHED,
};

// 同一标签下成因不同的 token：hover 解释按 token 覆盖（Could not verify 默认 tip 说的是「payer 拒了请求」，
// 对「payer 答了但没给信息」是假话）。列表 / 详情 / GP provider 卡片共用
export const ELIGIBILITY_TIP_BY_BACKEND_STATUS: Record<string, string> = {
    // 原型 tagTip 逐字（10/21）
    NO_COVERAGE_DETAIL:
        'The insurer answered but sent no coverage details, so we can’t tell whether the plan is active. ' +
        'This is not a denial.',
};

// 一次 check 可能同时命中多个状态，后端按它自己的优先级排好用逗号下发（§3.1）。哪个 token
// 当头条，前端按原型 tagState 的 if 顺序挑，而不是照单全收 tokens[0]——两套顺序在
// OUT_OF_NETWORK 上是反的（原型排第 2，后端排第 4），照搬会把「不在网络内」这种决定
// 能不能 billing 的事实，说成「算不出价格」。只在多 token 时与 tokens[0] 有分歧。
// tag 与 bar 共用这一张表：原型能渲染出来的每个场景两者都是对上的，各挑各的只会在
// 原型从未跑过的组合上造出「标签说网络、顶栏说金额」的卡片。
// 拒识类（含 UNMAPPED）一律优先：原型的 outcome==='rejected' 也在结果链之前
const HEADLINE_TOKEN_ORDER: string[] = [
    'INACTIVE',
    'OUT_OF_NETWORK',
    'SERVICE_NOT_COVERED',
    'CONFLICTING_AMOUNTS',
    // 这两条 tag 相同（都是 No cost estimate），顺序只对 bar 有意义：原型唯一真实跑过的
    // 叠加就是这一组（payer 一条 BH 福利都没返回 → 解析器读成疑似 carve-out），它的 bar
    // 取「缺价格」而不是「外包」——头条是拿不到价格，不是关于谁管 psych 的推断
    'NO_PER_VISIT_PRICE',
    'CARVE_OUT',
    // 同时 carve-out 时排在它之后：金额说的是别的 plan，Eligible 会是假话
    'PLANWIDE_ONLY',
    'ACTIVE',
];

export const headlineToken = (tokens: string[]): string | undefined => {
    const rejection = tokens.find((t) => ELIGIBILITY_REJECTION_STATUSES.has(t) || t === 'UNMAPPED');
    if (rejection) return rejection;
    return HEADLINE_TOKEN_ORDER.find((t) => tokens.includes(t)) || tokens[0];
};

// 列表筛选（/prospect/page eligibilityStatus 参数）：前端词汇表 → 后端枚举集合（含两个 sentinel）
export const ELIGIBILITY_FILTER_PARAM_BY_TAG: Partial<Record<EEligibilityStatus, string[]>> = {
    [EEligibilityStatus.ELIGIBLE]: ['ACTIVE', 'PLANWIDE_ONLY'],
    [EEligibilityStatus.NO_COST_ESTIMATE]: ['NO_PER_VISIT_PRICE', 'CARVE_OUT'],
    [EEligibilityStatus.OUT_OF_NETWORK]: ['OUT_OF_NETWORK'],
    [EEligibilityStatus.INACTIVE]: ['INACTIVE'],
    [EEligibilityStatus.SERVICE_NOT_COVERED]: ['SERVICE_NOT_COVERED'],
    [EEligibilityStatus.CONFLICTING_AMOUNTS]: ['CONFLICTING_AMOUNTS'],
    [EEligibilityStatus.COULD_NOT_VERIFY]: [
        'PATIENT_NOT_FOUND',
        'MEMBER_ID_ERROR',
        'DOB_MISMATCH',
        'DOB_MISSING',
        'GENDER_ON_POLICY',
        'NOT_SET_UP_WITH_PAYER',
        'PAYER_DOWN',
        'NO_COVERAGE_DETAIL',
    ],
    [EEligibilityStatus.CHECK_FAILED]: ['SYSTEM_ERROR'],
    [EEligibilityStatus.PAYER_NOT_MATCHED]: ['UNMAPPED'],
    [EEligibilityStatus.NEVER_CHECKED]: ['NEVER_CHECKED'],
    [EEligibilityStatus.NO_INSURANCE]: ['NO_INSURANCE'],
};

// CONFLICTING_AMOUNTS 的冲突原始行分组（COPAY / COINSURANCE 各一条）
export type TConflictBenefitVo = {
    field: 'COPAY' | 'COINSURANCE' | string;
    values?: Array<{
        coverageLevelCode?: string;
        serviceTypeCode?: string;
        // COPAY 行金额（美元）
        benefitAmount?: number;
        // COINSURANCE 行百分比（0-1 小数）
        benefitPercent?: number;
        // payer 附在该冲突行上的原文标签（271 additionalInformation[0].description，
        // 如 'OFFICE VISIT' / 'SPECIALIST VISIT'）；payer 未附时 null
        additionalInformationDescription?: string | null;
    }>;
};

// 后端 per-tile scope 的原始 token（与前端 EBenefitPeriod / ENetworkStatus 同拼写，适配层转枚举）
export type TBackendBenefitPeriod = 'VISIT' | 'CALENDAR_YEAR' | 'SERVICE_YEAR' | string | null;
export type TBackendNetworkTier = 'IN_NETWORK' | 'OUT_OF_NETWORK' | string | null;

// ProspectInsuranceEligibilityDetailVo：benefits 数据 + 文案动态参数（金额美元、时间 ISO）
export type TBackendEligibilityDetail = {
    payerName?: string;
    // Stedi 侧 payer 编号（如 '87726'）；现有 UI 无消费点
    payerId?: string;
    planName?: string;
    // payer 侧 plan 编号，与 planName 分立；现有 UI 无消费点
    planNumber?: string;
    // 页头 Group 号 → result.plan.group
    groupNumber?: string;
    planEffectiveStart?: string;
    planEffectiveEnd?: string;
    copayAmount?: number;
    // per-tile scope（raw_response 派生；null = payer 未答，scope 行对应段保持沉默）：
    // period 27→VISIT / 23→CALENDAR_YEAR / 22→SERVICE_YEAR；networkTier Y→IN / N→OUT
    copayPeriod?: TBackendBenefitPeriod;
    copayNetworkTier?: TBackendNetworkTier;
    // payer 附言（如 "after deductible"）→ tile 小字；null 时沿用前端默认文案
    copayNote?: string | null;
    // 一般医疗门诊 copay（STC=98 行的 code=B benefitAmount）。copayAmount 恒为 behavioral health
    // 那一行，两者在同一个 Copay 格里并排展示，provider 按这次服务的类型决定实收哪个。
    // payer 未提供 STC=98 行时为 null——缺席 ≠ 与 BH 同价
    medicalCopayAmount?: number;
    medicalCopayPeriod?: TBackendBenefitPeriod;
    medicalCopayNetworkTier?: TBackendNetworkTier;
    // 命中行的 additionalInformation.description（如 'OFFICE VISIT'），语义同 copayNote
    medicalCopayNote?: string | null;
    coinsurancePercent?: number;
    coinsurancePeriod?: TBackendBenefitPeriod;
    coinsuranceNetworkTier?: TBackendNetworkTier;
    coinsuranceNote?: string | null;
    deductibleIndividualTotal?: number;
    // payer YTD 已用（真值；缺省时前端才用 total-remaining 补算并标 calculated）
    deductibleIndividualMet?: number;
    deductibleIndividualRemaining?: number;
    deductibleIndividualPeriod?: TBackendBenefitPeriod;
    deductibleIndividualNetworkTier?: TBackendNetworkTier;
    // family 维度四组：原型/设计只有 Individual 卡，字段先进类型不消费（设计加 Family 卡即点亮）
    deductibleFamilyTotal?: number;
    deductibleFamilyMet?: number;
    deductibleFamilyRemaining?: number;
    deductibleFamilyPeriod?: TBackendBenefitPeriod;
    deductibleFamilyNetworkTier?: TBackendNetworkTier;
    oopIndividualTotal?: number;
    oopIndividualMet?: number;
    oopIndividualRemaining?: number;
    oopIndividualPeriod?: TBackendBenefitPeriod;
    oopIndividualNetworkTier?: TBackendNetworkTier;
    oopFamilyTotal?: number;
    oopFamilyMet?: number;
    oopFamilyRemaining?: number;
    oopFamilyPeriod?: TBackendBenefitPeriod;
    oopFamilyNetworkTier?: TBackendNetworkTier;
    bhCovered?: boolean;
    visitLimitTotal?: number;
    visitLimitRemaining?: number;
    // visit limit 计费周期（F 行 timeQualifier：23→CALENDAR_YEAR / 22→SERVICE_YEAR / 34→MONTH /
    // 27→VISIT；null = payer 未答）。VISIT 只表达单位不带周期，展示口径同 null（裸 "up to N visits"）
    visitLimitTimeQualifier?: TBackendBenefitPeriod;
    networkStatus?: 'IN_NETWORK' | 'OUT_OF_NETWORK' | null;
    // OUT_OF_NETWORK 命中的 payer 原文（raw_response 派生）→ network.payerMessage
    networkMessage?: string;
    // CARVE_OUT 命中的 marker 原文（如 'OPTUM'）→ carveOut.administratorName
    carveOutMarker?: string;
    carveOutKind?: 'PROGRAM_SPECIFIC' | 'OTHER_PAYER' | string;
    // ACTIVE + bhCovered 场景的服务类别（文档口径 PSYCHOTHERAPY / MENTAL_HEALTH，
    // 但前端按通用 token 转词显示，未知值同样可读）→ behavioralHealth.serviceType
    bhServiceType?: string;
    payerNote?: string;
    // payer 校正回的 subscriber 信息（可能与档案不同）；现有 UI 无消费点
    verifiedSubscriberFirstName?: string;
    verifiedSubscriberLastName?: string;
    verifiedSubscriberDob?: string;
    verifiedSubscriberGender?: string;
    verifiedSubscriberMemberId?: string;
    conflictFields?: string[];
    // CONFLICTING_AMOUNTS 的冲突原始行 → copay/coinsurance tile 的多值展示
    conflictBenefits?: TConflictBenefitVo[];
    priorAuthRequired?: boolean | null;
    // cost sharing 数字来源基准：BEHAVIORAL（BH STC 行）/ GENERAL（plan-wide 30 行）/ MIXED
    // （两者混合）/ null。GENERAL/MIXED → tiles 下方琥珀警示；BEHAVIORAL/null 沉默。
    // 后端当前实现只会输出 BEHAVIORAL/null（GENERAL/MIXED 等 plan-wide 回退上线），前端预埋
    costSharingBasis?: 'BEHAVIORAL' | 'GENERAL' | 'MIXED' | string | null;
};

// Discovery 候选（ProspectInsuranceEligibilityDiscoveryCandidateVo）
export type TBackendDiscoveryCandidate = {
    payerId: string;
    payerName: string;
    planType?: string | null;
};

// ProspectInsuranceEligibilityCheckVo：/check 填 detail、/discovery 填 discovery；
// prospect 列表/详情的 latestEligibilityCheck 也是这个形状（列表只填 status + latestCheckedAt，
// 详情路径额外填 failedCount/todayCheckCount/cashPay/insurancePay/detail，UNMAPPED 且跑过
// discovery 时连候选一起下发）
export type TBackendEligibilityCheckVo = {
    status?: string | null;
    latestCheckedAt?: string | null;
    // 榜出 record 上的保险快照：这次 check 当时用的 carrier/plan，不是患者档案的当前值
    // （患者换过卡时两者会不同——结果卡与列表都以这里为准，否则会把新保司名安在旧结果上）。
    // list 与 detail 路径都下发；匹配不到时为 null
    carrier?: string | null;
    plan?: string | null;
    // 最近一次 check 的归属 provider（仅 GP 列表/详情路径填，solo 路径恒 null）
    latestCheckedProviderId?: number | null;
    latestCheckedProviderFirstName?: string | null;
    latestCheckedProviderLastName?: string | null;
    // 实际操作人（trigger_user_id；owner 代查时 = owner，SYSTEM_AUTO_* 触发时 null）——
    // "Last checked by X" 用操作人名字；同样仅 GP 路径填
    latestCheckedOperatorId?: number | null;
    latestCheckedOperatorFirstName?: string | null;
    latestCheckedOperatorLastName?: string | null;
    // 连续 transport 失败次数（拿到任一 PASSED-group status 置零）；≥2 触发二次失败 outreach
    failedCount?: number | null;
    // 该 provider 今日（NY 时区）attempt 累计（含 cache hit + transport failure）；日限计数初始值
    todayCheckCount?: number | null;
    // 失败 ≥2 次时的文案分歧：最早的 upcoming PPA insurance 预约对应服务项的 cash/insurance 开关；
    // 客户无 upcoming PPA 预约时均为 null
    cashPay?: boolean | null;
    insurancePay?: boolean | null;
    detail?: TBackendEligibilityDetail | null;
    discovery?: TBackendDiscoveryCandidate[] | null;
    networkStatusReason?: TNetworkStatusReason | null;
    networkStatusReasonPlanType?: string | null;
    insuranceId?: number | null;
    insuranceUpdatedAt?: string | null;
};

export type TNetworkStatusReason =
    | 'IN_NETWORK'
    | 'STEDI_SAID_OUT'
    | 'PROVIDER_NO_MATCHING_PLAN'
    | 'COVERAGE_INACTIVE'
    | 'UNKNOWN';

// EligibilityPrecheckVo：三个 flag 各对应一种 setup gap，全 true 才允许调 /check
export type TEligibilityPrecheckVo = {
    eligibilityCheckEnabled?: boolean;
    billingNpiConfigured?: boolean;
    profileNpi?: string;
    payerIdMapped?: boolean;
    // 该 provider 是否在 uniprofile 里声明接受 (prospect.state, insurance.carrier)。
    // 与 payerIdMapped **相互独立**（一个是「接不接这家保险」，一个是 owner 填没填 payer ID），
    // 后端明确不 gate /check —— 拦截是前端策略：没有这一行，payer ID 无处可填、设置弹窗的
    // grid 也插不出缺行，所以出 template gap 横幅并拦住 check（2026-09-15 拍板）。
    // 字段为 undefined（老后端/灰度）时不拦，与今天行为一致
    providerAcceptsInsurance?: boolean;
    // 本次 payer 走的通道（Medicare 判定在后端）：决定 payerIdMapped 校验的是哪个 payer ID 字段，
    // 也决定设置弹窗 blocking 格子的 "· this check" 落在 commercial 还是 government 输入框
    payerIdKind?: 'COMMERCIAL' | 'GOVERNMENT' | string;
    // 本 NY 日历月已产生的 billable eligibility 费用（美元；个人口径——override 传谁查谁的）
    // → cost modal 累计行
    currentMonthFee?: number;
};

// ---- Provider notification（红点通知，§8）----

export type TEligibilityNotificationType = 'PATIENT_INSURANCE_UPDATED' | 'GROUP_MEMBER_PAYER_ID_SET_UP';

// items/{type} 只返回未读条目；保存 payer-config 成功后后端自动按 provider 置读（3.2 副作用），
// 前端只需重查，无需调 read 接口
export type TProviderNotificationItemVo = {
    id: number;
    notificationType: string;
    sourceUserType?: string | null;
    sourceUserId?: number | null;
    bizRefType?: string | null;
    bizRefId?: number | null;
    // GROUP_MEMBER_PAYER_ID_SET_UP: { providerId, firstName, lastName }
    metaData?: Record<string, unknown> | null;
    readAt?: string | null;
    createAt?: string;
};

// ---- To-do "Insurance to re-check"（§2.7）：行 = 一场等 provider 复查的未来 in-network 预约。
// 只回当前登录 provider 的 action-state 行，后端按 startAt ASC 排、滤掉已取消/已开始；
// provider 点 Check 后（无论结果）后端清 action-state，行不再返回 ----
export type TRecheckAppointmentVo = {
    appointmentId: number;
    clientId: number;
    // 该 client 顶栏（min sort）insurance id；null = 已无 active 保险行（后端会自动清这条）
    insuranceId?: number | null;
    patientFirstName?: string;
    patientLastName?: string;
    appointmentName?: string;
    specialty?: string;
    serviceType?: string;
    // ISO 无时区 = UTC
    startAt: string;
    // 分钟
    duration?: number;
};

// ---------- Client detail 页 eligibility 模块的前端展示契约 ----------
// 适配层（api/eligibility/adapt.ts）把后端 Vo 转成下面的展示模型；UI 组件只消费这一层。
// tag（EEligibilityStatus）一律按后端 primary status 映射；stale 只影响 last-checked 行的颜色，不改 tag。

// 新鲜窗口：超过即 stale。只用于结果卡 last-checked 行的琥珀提醒，不再参与任何 tag 派生
export const ELIGIBILITY_FRESHNESS_WINDOW_DAYS = 7;

export enum EEligibilityOutcome {
    OK = 'OK',
    REJECTED = 'REJECTED',
}

export enum ECoverageStatus {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    UNKNOWN = 'UNKNOWN',
}

export enum EBenefitPeriod {
    CALENDAR_YEAR = 'CALENDAR_YEAR',
    SERVICE_YEAR = 'SERVICE_YEAR',
    VISIT = 'VISIT',
    // 仅 visitLimitTimeQualifier 会给（34→MONTH）；tile scope 三值不含它
    MONTH = 'MONTH',
}

export enum ECoverageLevel {
    INDIVIDUAL = 'INDIVIDUAL',
    FAMILY = 'FAMILY',
}

export enum ENetworkStatus {
    IN_NETWORK = 'IN_NETWORK',
    OUT_OF_NETWORK = 'OUT_OF_NETWORK',
    UNKNOWN = 'UNKNOWN',
}

// tiles 的 scope 行（"Calendar year · Individual · In network"）：payer 没说的轴不下发，前端保持沉默
export type TBenefitScope = {
    period?: EBenefitPeriod;
    coverageLevel?: ECoverageLevel;
    networkTier?: ENetworkStatus.IN_NETWORK | ENetworkStatus.OUT_OF_NETWORK;
};

// copay / coinsurance（单次费用）
export type TVisitCostTile = {
    // false → "Not reported by payer" 斜纹态，其余字段不下发。缺失 ≠ $0 ≠ 不覆盖
    reported: boolean;
    kind?: 'AMOUNT' | 'PERCENT';
    // AMOUNT 单位美分；PERCENT 整数（20 = 20%）。length > 1 = payer 自相矛盾，
    // 前端渲染 "$30 or $60 · payer sent both"，不替 payer 选一个
    values?: number[];
    // 与 values 逐位对齐的 payer 原文标签（如 'OFFICE VISIT'；该行没附则 null）——
    // 冲突面板每行 "the payer labelled this row …" 引用；仅冲突时下发
    valueLabels?: Array<string | null>;
    scope?: TBenefitScope;
    // payer 附言（如 "after deductible"），前端小字原样渲染
    note?: string;
};

// deductible / oopMax（累计额度）
export type TAccumulatorTile = {
    reported: boolean;
    totalCents?: number;
    metCents?: number;
    remainingCents?: number;
    // 后端由另两个字段补算出的值 → 前端标 "(calculated)"
    derived?: 'REMAINING' | 'MET';
    scope?: TBenefitScope;
};

export type TCostSharing = {
    // behavioral health 的单次自付额。与 medicalCopay 同格并排（两个数回答的是同一个问题
    // 「这次该收多少」），不是两类东西
    copay: TVisitCostTile;
    // 一般医疗门诊的同一问题。reported=false 时 Copay 格退回单数形态——payer 没给第二个数时
    // 并排一个空列只是噪音
    medicalCopay: TVisitCostTile;
    coinsurance: TVisitCostTile;
    deductible: TAccumulatorTile;
    oopMax: TAccumulatorTile;
    // 数字来源基准（detail.costSharingBasis 原样）：GENERAL/MIXED 时 tiles 下方渲染琥珀警示
    // （carve-out 块在场时抑制——它把同一件事说得更重）；BEHAVIORAL/缺省沉默
    basis?: 'BEHAVIORAL' | 'GENERAL' | 'MIXED' | string;
};

export type TBehavioralHealth = {
    covered: boolean;
    // payer 答复的服务类别 token（PSYCHOTHERAPY / MENTAL_HEALTH / 其他），驱动绿框标题措辞；
    // 缺省时标题兜底 Psychotherapy
    serviceType?: string;
    // "allows up to N visits, and the payer did not say how many are left"——绝不当剩余次数展示
    visitLimit?: { quantity: number; period?: EBenefitPeriod; note?: string };
    // 存在即 carve-out（BH 委托给另一家管理），琥珀框点名管理方，上方金额不适用于本 visit
    carveOut?: { administratorName?: string };
};

export type TEligibilityResult = {
    coverageStatus: ECoverageStatus;
    // 271 回的 payer 名（detail.payerName）：BH 块 / SNC 行点名用，展示前过 payerProse
    payer?: { name?: string };
    plan?: { name?: string; group?: string; startDate?: string; endDate?: string };
    // INACTIVE 时不下发（前端也不渲染 tiles）
    costSharing?: TCostSharing;
    behavioralHealth?: TBehavioralHealth;
    // benefit 行的 in-network 标记不构成 network 断言；只有 payer 明文才给非 UNKNOWN
    network?: {
        status: ENetworkStatus;
        payerMessage?: string;
        // PROVIDER_NO_MATCHING_PLAN 时 = plan type 展示名（如 'Medicare Advantage'）：OON 是平台按
        // service template 判的，不是 payer 说的，文案要点名原因
        planTypeNotOnTemplate?: string;
    };
    // payer 未返回 auth 指示时整个字段缺省（absence ≠ not required，FR11）
    priorAuth?: { required: boolean; note?: string };
    // 存在即「本服务被计划明确排除」（排除 ≠ 未定价）
    serviceNotCovered?: { note?: string };
    notes?: string[];
};

export type TEligibilityRejection = {
    // 后端 status 串里的拒识类 token（ELIGIBILITY_REJECTION_STATUSES，保持后端优先级序，可能多个）。
    // 前端持码表（aaa.ts，按枚举 keyed）做文案/修复建议/按钮/多码合并；未知 token 兜底通用文案
    codes: string[];
    // 无码或未知码时的兜底摘要（payer 原话/后端归纳）
    reasonSummary?: string;
};

// unmapped payer 的 advanced search 候选（付费目录搜索的返回）
export type TPayerCandidate = {
    name: string;
    payerId: string;
    note?: string;
};

export type TEligibilityCheckVo = {
    // ISO；缓存命中保留原始时间戳，绝不是 "now"
    checkedAt: string;
    // 这次 check 跑的是哪份保险（后端 record 快照）；患者换卡后与档案上的当前保险不同
    carrier?: string;
    plan?: string;
    // 答的是档案上哪一张（prospect_insurance.id）：结果卡里的「<carrier> · Primary/Secondary insurance」按它找卡
    insuranceId?: number;
    // 实际操作人姓名（"Last checked by Sarah Kim"）；仅 GP 路径下发/填充，solo 恒空 → 不显示 by
    checkedBy?: string;
    // 后端 status 串拆出的 token 列表（优先级序，首个为 primary）；tag/bar 场景优先按它判定
    statusTokens?: string[];
    outcome: EEligibilityOutcome;
    result?: TEligibilityResult;
    rejection?: TEligibilityRejection;
};

// 进 detail 页一次拿全的状态（含 idle 分支所需上下文）
export type TClientEligibilityStateVo = {
    // 缺省 = never checked
    latestCheck?: TEligibilityCheckVo;
    // managed idle 分支：有 booking → 自动查询只读说明；无 → 免费 Check 按钮
    hasUpcomingBooking: boolean;
    // managed 每日手动 re-check 限额（5 次/患者/天）；剩余次数永不下发
    recheckAllowed: boolean;
    recheckDeniedReason?: 'DAILY_LIMIT';
    // cost modal 的「本月累计」行（美分，普通 check 与 advanced search 合计）
    monthToDateCents: number;
    // 存储的 carrier 无法匹配 clearinghouse payer（FR4，后端 UNMAPPED）：请求不会发出。
    // 候选列表不在这里——那是付费 discovery 的返回（runEligibilityDiscovery）
    // insuranceId：被挡下的是哪张卡（「查另一张」要排除它，要卡文案点名它）
    payerUnmatched?: { carrierOnFile?: string; insuranceId?: number };
    // ── check 发出前的设置门槛（manual persona；managed 的设置 Klarity 已持有，不下发）──
    // billing NPI 是 practice 级（一练所一组织 NPI）；payer ID 是 provider 级
    // （挂各 provider 的 service template：state × carrier × commercial/government）
    setup?: {
        // false → 首次 check 前弹 NPI 步（member 无权设，toast 让 owner 先设）
        billingNpiSet: boolean;
        // NPI 步 radio 的档案选项（Klarity 从 credentialing 持有的 rendering NPI）
        profileNpi?: { npi: string; name: string };
        // 本患者 carrier 在目录里、但该 provider 没配 payer ID → 弹 payer 步 / member 出 gap 横幅。
        // 与 payerUnmatched（目录解析不到 → advanced search）是两种不同的 blocked，互斥下发
        payerIdMissing?: { carrier: string; state: string; kind: 'commercial' | 'government' };
        // provider 没声明接这家保险（precheck.providerAcceptsInsurance=false）→ 连格子都不存在，
        // payer ID 横幅/设置弹窗都无处落笔，改出 template gap 横幅（去 service template 页加 carrier）。
        // 与 payerIdMissing 展示上互斥且优先级更高（两个字段本身在后端是独立的）
        templateMissing?: { carrier: string; state: string };
    };
    // 二次失败（仅 managed persona 消费；失败政策 SOP 的 scope 就是 billing program 内的 provider）：
    // 由后端 failedCount >= 2 派生（连续 transport 失败，拿到 PASSED-group status 置零）。
    // cashPay 来自最早的 upcoming PPA insurance 预约的服务项开关；客户无 upcoming PPA 预约时为
    // undefined —— strip 照显（核心信息是"查了两次还是不行"），结尾走 no-cash 版
    outreach?: {
        stillFailing: boolean;
        cashPay?: boolean;
    };
};

// ---------- check history（标题行 History 弹窗，后端 §2.8） ----------

// 后端 EligibilityCheckHistoryItemVo。数据源是 append-only 的 attempt log
// （insurance_eligibility_check_record）：一行 = 一次 attempt，含 cache hit 与 transport failure；
// discovery 不落这张表，所以 $1.50 的 payer search 不会出现在流水里（发票里有，两处口径不同）
export type TBackendEligibilityHistoryItem = {
    id: number;
    // attempt 当时的保险快照
    carrier?: string | null;
    plan?: string | null;
    // 10/21：attempt 当时这张卡在档案上的槽位（快照，之后改主次不影响）
    insuranceCardType?: 'PRIMARY' | 'SECONDARY' | null;
    // 关联 result 的 **primary token**（单个，不是逗号串）；result 行写失败时为 null
    resultStatus?: string | null;
    checkedAt?: string | null;
    // 实际操作人（trigger_user_id）；SYSTEM_AUTO_* 触发或历史缺失时为 null
    checkerUserId?: number | null;
    checkerFirstName?: string | null;
    checkerLastName?: string | null;
    checkerRole?: string | null;
    // 被查 provider（record.provider_id）：OWNER / ADMIN 代查时与操作人不同
    targetProviderId?: number | null;
    targetProviderFirstName?: string | null;
    targetProviderLastName?: string | null;
};

// 流水的一行（展示模型）。范围是 (当前 provider, 当前 patient) 这一对——practice 视图下
// 「当前 provider」是被查的那位（请求带 OVERRIDE-PROVIDER-ID），切 provider 卡流水跟着换。
// 哪些 attempt 进流水由后端定，前端不过滤
export type TEligibilityCheckHistoryRow = {
    id: number;
    // ISO
    checkedAt: string;
    // 后端 primary token，经 ELIGIBILITY_TAG_BY_BACKEND_STATUS 转词汇表；缺省 → 不渲染徽章
    status?: string;
    carrier?: string;
    plan?: string;
    // History 的 Insurance type 列；后端缺省时留破折号
    insuranceCardType?: 'PRIMARY' | 'SECONDARY';
    // 按下按钮的人（SYSTEM 触发时后端无名字，前端也不编）
    checkerName?: string;
    checkerRole?: TEligibilityCheckerRole;
    // 这次 check 以谁的身份发出；与 checkerName 不同才印第二行（owner/admin 代查）
    targetProviderName?: string;
};

// 后端 §2.8 下发的是 role.name（klarity_organization_member.role_id → role.name），
// 不是 is_owner 之类的权限位——角色分类以这张表为准
export type TEligibilityCheckerRole =
    | 'GROUP_OWNER'
    | 'GROUP_PROVIDER'
    | 'GROUP_PRACTICE_MANAGER'
    | 'GROUP_ADMINISTRATIVE_ASSISTANT'
    | 'GROUP_CLINICAL_ASSISTANT'
    // trigger_user_id 为 null / 找不到 org member / role 已删（历史脏数据兜底）
    | 'SYSTEM';

// 计费单价（美分）。仅供 UI 说明文案使用（cost modal / tip），不参与任何求和——发票金额以后端为准。
// 已拍板：每次 check 都收费（含缓存命中）；managed persona 的普通 check 免费
export const ELIGIBILITY_PRICE_CENTS = 25;
// advanced payer search：全目录搜索，所有 practice 都收费（含 Managed Billing，已与 Product 确认按原型）
export const ELIGIBILITY_ADVANCED_PRICE_CENTS = 150;

// ---------- payer ID / NPI 设置流（provider 级 payer ID + practice 级 billing NPI） ----------

// grid 的一行：一个 provider 在一个 state 下对一个 carrier 的配置。
// state/carrier 骨架来自该 provider 自己的 service template 勾选（与 uniprofile insurance survey 同源）
export type TPayerIdEntry = {
    carrier: string;
    state: string;
    // plan 列表（来自 insurance survey，3.1 下发）；设置弹窗里只读展示，不提供修改入口（2026-09-08 拍板）
    plans: string[];
    commercialPayerId?: string;
    governmentPayerId?: string;
};

export type TPracticeProvider = {
    providerId: number;
    name: string;
    // 该 provider service template 覆盖的州（owner grid 的 chip 过滤用）
    states: string[];
    // 当前登录者本人
    self?: boolean;
};

// team request 已切通知驱动（GROUP_MEMBER_PAYER_ID_SET_UP，§8）：新 member 首次保存 template
// 触发、owner 收横幅；payer-config 保存成功后后端自动按 provider 置读。本地占位 store 已退役

// ---- /provider/insurance/payer-config 后端形状 ----

// InsurancePayerVo（读侧；group owner 拿全 org active provider，其余只拿自己）
export type TInsurancePayerVo = {
    carrier: string;
    plans?: string[];
    state: string;
    insuranceType?: string;
    commercialPayerId?: string;
    governmentPayerId?: string;
};

export type TProviderPayerConfigVo = {
    providerId: number;
    firstName?: string;
    lastName?: string;
    insurances?: TInsurancePayerVo[];
};

// ProviderInsurancePayer（写侧；整份数组全量替换该 provider 名下映射——漏传条目会被删）。
// 写侧无 plans 字段，与前端一致：plan 在设置弹窗里只读展示（2026-09-08 拍板），无需写入口
export type TProviderInsurancePayerReq = {
    providerId: number;
    state: string;
    carrier: string;
    commercialPayerId?: string;
    governmentPayerId?: string;
};

// EligibilitySetupReq（2.1 /setup：一次性保存 billing NPI 并打开 org 级开关；payer 增量走 payer-config）
export type TEligibilitySetupReq = {
    billingNpi?: string;
    billingTaxonomyCode?: string;
    providerPayers?: Array<{
        providerId: number;
        payers?: Array<{
            state?: string;
            carrier?: string;
            commercialPayerId?: string;
            governmentPayerId?: string;
        }>;
    }>;
};

// Clients 列表的行级字段（latestEligibilityCheck）直接挂在 types/common.ts 的 Prospect 上，
// 列表路径只填 status + latestCheckedAt。

// ---------- Financials → Eligibility checks 发票（/provider/insurance-billing/eligibility/invoices）----------

// 通用 mybatis-plus 分页壳
export type TIPage<T> = {
    records: T[];
    total: number;
    size: number;
    current: number;
};

// 后端 EligibilityInvoiceService 直接透传 provider_appointment_billing.billing_status，
// **不是** invoice 表的 INITIAL/SENT/PAID/OVERDUE（那套是 managed billing fee 月账单的，见
// financials.ts 的 TInvoiceStatus）。eligibility 发票是自动扣卡，没有「到期未付」这个状态，
// 所以这里没有 OVERDUE —— 扣款失败是 FAILED。同一枚举在 financials.ts 也声明过
// （ECredentialingDepositBillingStatus），改动时两边保持一致。
// INVOICE_PENDING = 当月未出账的虚拟合计行（invoiceId/issueDate/dueDate 为 null，恒置顶、不受筛选影响），
// 是前端契约值，不来自库里的 billing_status。
export type TEligibilityInvoiceStatus =
    | 'PENDING'
    | 'PROVIDER_NOT_READY'
    | 'PROCESSING'
    | 'SUCCEEDED'
    | 'FAILED'
    | 'CANCELED'
    | 'INVOICE_PENDING';

// EligibilityInvoiceSummaryVo（金额美元）
export type TEligibilityInvoiceSummaryVo = {
    feeTotal?: number;
    checksRun?: number;
    payerSearches?: number;
};

// EligibilityInvoiceListItemVo
export type TEligibilityInvoiceListItem = {
    // 虚拟 INVOICE_PENDING 行为 null（详情走 /unbilled，不渲染 PDF 入口）
    invoiceId: number | null;
    // INV-YYYYMM-EC
    invoiceNumber: string;
    billingFrom: string;
    billingTo: string;
    // 尝试次数（含缓存命中）；billable = 实际计费行数（= 详情 lines.length）
    checksRun: number;
    billable: number;
    // 美元
    fee: number;
    // 徽章映射交给前端，见 EligibilityCheckInvoices 的 BADGE_BY_STATUS
    status: TEligibilityInvoiceStatus;
    issueDate: string | null;
    dueDate: string | null;
    // ---- practice 列表（§1.7）追加的归属 provider 字段；个人列表（§1.2）不下发，存量行可能缺 ----
    providerId?: number | null;
    // 头衔（如 MD / NP）
    providerTitle?: string | null;
    providerAvatar?: string | null;
    providerName?: string | null;
    providerCredential?: string | null;
};

// EligibilityInvoiceDetailVo.Line：每行 = 一次 billable Stedi 调用。
// TODO(eligibility 二轮): 后端补 ranBy（provider 标识）+ advancedSearch 标志后恢复 Ran-by 列/分账块
export type TEligibilityInvoiceLine = {
    recordId: number;
    // 后端脱敏后的患者名（"Nadia F*"）
    clientName: string;
    payerName: string;
    // ISO
    checkAt: string;
    // EligibilityStatusEnum 值，经 ELIGIBILITY_TAG_BY_BACKEND_STATUS 转前端词汇表渲染
    checkStatus: string;
    // 该次调用出账时刻的价格快照（美元）
    fee: number;
};

export type TEligibilityInvoiceDetailVo = {
    // /unbilled 返回恒为 null
    invoiceId: number | null;
    invoiceNumber: string;
    billingFrom: string;
    billingTo: string;
    issueDate: string | null;
    dueDate: string | null;
    // 总额（美元）= sum(lines[].fee)
    fee: number;
    status: TEligibilityInvoiceStatus;
    lines: TEligibilityInvoiceLine[];
};

export type TEligibilityInvoiceSortType = 'BILLING_PERIOD' | 'CHECKS_RUN' | 'BILLABLE' | 'FEE' | 'ISSUED' | 'DUE';

export type TEligibilityInvoiceListReq = {
    current?: number;
    size?: number;
    sortType?: TEligibilityInvoiceSortType;
    orderType?: 'asc' | 'desc';
    invoiceNumber?: string;
    status?: TEligibilityInvoiceStatus[];
    // 语义同 summary（null/0 = 全部历史）；虚拟 INVOICE_PENDING 行不受该参数影响
    timeRangeMonths?: number;
    // provider 名模糊匹配——仅 practice 列表（§1.7 POST /practice/list）支持
    providerName?: string;
};
